import { adValues } from '@tiger/ads';
import { encryptLoginPassword } from '@tiger/api-signature';
import { trackPageview } from '@tiger/track';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { FormControl, FormLabel } from 'react-bootstrap';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from 'components/base/Button';
import Dialog from 'components/base/Dialog';
import Captcha, { CaptchaType } from 'components/Captcha';
import FormGroup from 'components/FormGroup';
import Email from 'components/forms/Email';
import Password from 'components/forms/Password';
import TelcodesPhone from 'components/forms/TelcodesPhone';
import OpenAddition from 'components/OpenAddition';
import Toast from 'components/Toast';
import SMSCode from 'components/VerifyCodeSend';
import security, { FB_PIXEL_ID_MAP } from 'stores/security';
import UI from 'stores/ui';
import { RegisterTypeEnum } from 'types/common';
import showError from 'utils/form/showFieldError';
import { reportEventList } from 'utils/gtag';
import { language } from 'utils/i18n';
import patterns from 'utils/patterns';
import { useTrack } from 'utils/track';
import URL from 'utils/URL';
import withProgressForm, { ProgressFormProps } from 'utils/withProgressForm';
import './style.scss';

/**
 * @description 原index页面用作路由入口，此组件为Signup页面
 */
const Page: React.FC<ProgressFormProps> = ({ $formutil }) => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { regType = RegisterTypeEnum.EMAIL } = useParams<{ regType: RegisterTypeEnum }>();
    const { $errors, $invalid, $params, $batchState, $getField, $getFirstError } = $formutil;
    const [loading, setLoading] = useState(false);
    const [showCaptcha, setShowCaptcha] = useState(false);
    const captchaRef = useRef<any>();
    const queryEvent = searchParams.get('event');
    const eventParmasModel = regType === RegisterTypeEnum.EMAIL ? '邮箱注册页' : '手机注册页';
    const { trackEvent, debounceTrackEvent } = useTrack();
    const onSubmit = async (ev?: React.FormEvent) => {
        ev?.preventDefault();

        reportEventList('signup_verification', 'signup');

        if ($invalid) {
            $batchState({
                $dirty: true,
                $touched: true
            });

            Toast.show($getFirstError());
        } else {
            setLoading(true);

            trackEvent({
                model: eventParmasModel,
                name: '点击注册按钮'
            });

            // 精准埋点，新 facebook pixel 的 注册按钮点击事件
            window.fbq?.('trackSingle', FB_PIXEL_ID_MAP.new, 'Lead');

            const {
                utm_source,
                utm_campaign,
                utm_campaign_id,
                utm_content,
                utm_term,
                utm_medium,
                adgroup_id,
                ad_id,
                keyword_id,
                external_id,
                external_client_id
            } = adValues;

            const signupParams: any = {
                ...$params,
                external_id,
                external_client_id,
                sem: {
                    source: utm_source,
                    campaign: utm_campaign,
                    campaign_id: utm_campaign_id,
                    content: utm_content,
                    term: utm_term,
                    medium: utm_medium,
                    adgroup_id,
                    ad_id,
                    keyword_id
                }
            };

            if ($params.login_password) {
                signupParams.login_password = encryptLoginPassword($params.login_password);
                signupParams.pwd_api_ver = 'v2';
            }

            delete signupParams.confirm_password;

            // 如果有邀请码，params 加入 invite_code https://wiki.tigerbrokers.net/pages/viewpage.action?pageId=20611482 @云为国
            if (adValues.invite) {
                signupParams.invite_code = adValues.invite;
            }

            // 如果有adcode，params 加入
            if (adValues.adcode) {
                signupParams.adcode = adValues.adcode;
            }

            // 邮箱注册的话加上process=signup
            if (regType === RegisterTypeEnum.EMAIL) {
                signupParams.process = 'signup';
                signupParams.type = RegisterTypeEnum.EMAIL;
            }

            try {
                // outh 接口granttype: 'phone' || 'email'
                await security.signup(signupParams, regType);

                reportDataEventSignup(regType);

                const step_1_callback = searchParams.get('step_1_callback');
                const next = searchParams.get('next');
                const nextUrl = step_1_callback || next;

                //  如果设置了回调，则注册完成后回调地址
                if (nextUrl && patterns.isSafeUrl(nextUrl)) {
                    if (queryEvent === 'share') {
                        return (window.location.href = `${nextUrl}#${queryEvent}?invite=${adValues.invite}`);
                    }

                    window.location.href = nextUrl;

                    return;
                }

                // 注册后需要获取用户状态信息
                try {
                    await security.getStatus();

                    navigate('/open');
                } catch (error) {
                    // 如果获取状态失败，需要提醒用户去登录个人中心继续
                    await Dialog.alert({
                        title: 'System error',
                        content: error.message,
                        hideCloseBtn: true
                    });

                    return (window.location.href = '/profile');
                }
            } catch (error) {
                // 需要进行人机验证
                if (error.error_code === 11011) {
                    setShowCaptcha(true);

                    return;
                }

                // 提交接口出现错误并且有人机验证则重置
                if (captchaRef.current) {
                    captchaRef.current?.reset();
                }

                handleError(error);
            }

            return setLoading(false);
        }
    };

    const handleError = error => {
        const verifyCodeFieldUtil = $getField('verify_code');
        let errMsg = error.error_msg;

        switch (Number(error.error_code)) {
            case 11043:
                errMsg = __('This code has expired. Please request a new one');
                showError(verifyCodeFieldUtil, errMsg);
                break;
            case 11044:
                errMsg = __('Invalid code. Please check your code and try again');
                showError(verifyCodeFieldUtil, errMsg);
                break;
            default:
                Toast.show(errMsg);
                break;
        }
    };

    const reportDataEventSignup = (regType?: string) => {
        // google
        trackPageview('/accounts/signup', 'signup');

        // 手机和邮箱,注册按钮
        reportEventList(`signup_${regType}` as any, 'signup');
        // end

        // facebook
        if (window.fbq) {
            window.fbq('trackSingle', FB_PIXEL_ID_MAP.old, 'CompleteRegistration');

            // 事件名称是 CompleteRegistration ? 如果改为 CompleteRegistration
            // 则无需使用 trackSingle , 使用 track 即可
            // 精准埋点，新 facebook pixel 的 注册成功事件
            window.fbq('trackSingle', FB_PIXEL_ID_MAP.new, 'COMPLETE_REGISTRATION');
        }
        // end

        // Twitter
        if (window.twq && adValues.invite && UI.twitterCodes[adValues.invite]) {
            window.twq('track', 'CompleteRegistration');
        }
        // end
    };

    const handleClick = () => {
        const verifyCodeFieldUtil = $getField('verify_code');

        verifyCodeFieldUtil?.$reset();
    };

    useEffect(() => {
        // 谷歌默认校验成功用户是无感的 因此不需要展示人机校验
        if ([CaptchaType.RECAPTCHA_SCORE].includes(captchaRef.current?.captchaType)) {
            setShowCaptcha(false);

            captchaRef.current = null;
        }
    }, [captchaRef.current?.captchaType]);

    return (
        <form className="signup-page-root" noValidate onSubmit={onSubmit}>
            <OpenAddition />
            {regType === RegisterTypeEnum.EMAIL ? (
                <Fragment>
                    <Email
                        name="username"
                        label={
                            <div className="form-header">
                                <FormLabel>{__('Sign up to start a new application')}</FormLabel>
                                <Link
                                    onClick={handleClick}
                                    className="reg-link"
                                    to={{ search: location.search, pathname: '/signup/phone' }}>
                                    {__('Sign up with phone number')}
                                </Link>
                            </div>
                        }
                        $onFieldChange={() => {
                            debounceTrackEvent({
                                model: eventParmasModel,
                                name: '输入电子邮件'
                            });
                        }}
                    />
                    <Password
                        eventParams={{
                            loginPassword: {
                                model: eventParmasModel,
                                name: '输入密码'
                            },
                            confirmPassword: {
                                model: eventParmasModel,
                                name: '确认密码'
                            }
                        }}
                    />
                </Fragment>
            ) : (
                <Fragment>
                    <TelcodesPhone
                        phoneOptions={{
                            name: 'username'
                        }}
                        label={
                            <div className="form-header">
                                <FormLabel>{__('Sign up to start a new application')}</FormLabel>
                                <Link
                                    onClick={handleClick}
                                    className="reg-link"
                                    to={{ search: location.search, pathname: '/signup/email' }}>
                                    {__('Sign up with email address')}
                                </Link>
                            </div>
                        }
                        eventParams={{
                            telCode: {
                                model: eventParmasModel,
                                name: '切换区号'
                            },
                            phone: {
                                model: eventParmasModel,
                                name: '输入电话号码'
                            }
                        }}
                    />

                    <SMSCode
                        type="phone"
                        process="verify_code_signin"
                        disabled={!!$errors.username}
                        username={$params.username}
                        tel_code={$params.tel_code}
                        eventParams={{
                            sendVerifyCode: {
                                model: eventParmasModel,
                                name: '获取验证码'
                            },
                            inputVerifyCode: {
                                model: eventParmasModel,
                                name: '输入验证码'
                            }
                        }}
                    />
                </Fragment>
            )}

            {/* 没有邀请码链接的手动输入 */}
            {!adValues.invite && (
                <FormGroup
                    name="invite_code"
                    label={__('Invite Code')}
                    checker={value => {
                        if (!value) {
                            return true;
                        }

                        return patterns.isInviteCode(value) || __('The invite code is not valid');
                    }}>
                    <FormControl placeholder={__('Invite Code (If Applicable)')} autoComplete="off" />
                </FormGroup>
            )}

            {showCaptcha && (
                <Captcha
                    action="signup"
                    ref={captchaRef}
                    onClose={() => {
                        setShowCaptcha(false);
                    }}
                    onSuccess={() => {
                        onSubmit();
                    }}
                />
            )}

            <Button
                block
                type="primary"
                size="lg"
                htmlType="submit"
                className="btn-submit"
                loading={loading}
                disabled={loading}>
                {__('Sign Up')}
            </Button>

            {queryEvent !== 'share' && (
                <div className="text-meta text-end">
                    {__('Already started?')}
                    <a
                        href={URL.merge('/login', { query: URL.current().query })}
                        className={language.startsWith('en') ? 'ml-1' : ''}>
                        {__('Log in to continue your application.')}
                    </a>
                </div>
            )}
        </form>
    );
};

export default withProgressForm({
    step: 0,
    basePath: 'open'
})(Page);
